import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Input,
  Checkbox,
  Table,
  Tbody,
  Tr,
  Td,
  useToast,
  Th,
  Thead,
  Flex,
  FormControl,
  Box,
  Button,
  HStack,
  Select,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import "./usergroups.css";
import { useMsal } from "@azure/msal-react";
import { useAppContext } from "../../../context/AppContext";
import {
  fetchUserGroupQuickLinkGroupMapping,
  updateQuickLinksGroupMapping,
} from "../../../services/AdminServices/UserGroupQuickLinkGroupMapping";
import { UserQuicklinkGroupMapping } from "../../../models/UserQuicklinkGroupMapping";
interface QuickLinksModalProps {
  isOpen: boolean;
  onClose: () => void;
  userGroupId: number |0;
  quickLinks: any[]; // Refine this to match the structure of quick links
  onSave: (updatedQuickLinks: any[]) => void; // Refine 'any' if possible
}

const QuickLinksModal: React.FC<QuickLinksModalProps> = ({
  isOpen,
  onClose,
  userGroupId,
  quickLinks,
  onSave,
}) => {
  const [quickLinkGroup, setQuickLinkGroup] = useState<UserQuicklinkGroupMapping[]>([]);
  const [allChecked, setAllChecked] = useState<boolean>(false); // Header checkbox state
  const [selectedLinks, setSelectedLinks] = useState<number[]>([]);

  // api integration
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast(); // To show toasts

  // tenant id
  const { accessToken, userData } = useAppContext();
  const tenantID =userData?.tenantID || 0;

  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [currentPage, setCurrentPage] = useState(1); // Track the current page
  const [itemsPerPage, setItemsPerPage] = useState(5); // Items per page

  // Handle individual link toggling
  const handleToggleLink = (quickLinksGroupID: number) => {
    setSelectedLinks((prev) =>
      prev.includes(quickLinksGroupID)
        ? prev.filter((id) => id !== quickLinksGroupID)
        : [...prev, quickLinksGroupID]
    );
  };

  // Toggle all quick links
  const handleToggleAllLinks = () => {
    const allQuickLinkIDs = filteredQuickLinks.map(
      (link) => link.projectLinkGroupID
    ); // Use quickLinksGroupID directly

    if (selectedLinks.length === quickLinkGroup.length) {
      // Uncheck all quick links
      setSelectedLinks([]);
    } else {
      // Check all quick links
      setSelectedLinks(allQuickLinkIDs);
    }
  };

  const handleSaveQuickLinks = async () => {
    if (!accessToken) {
      toast({
        position: "top",
        title: "Authentication Error",
        description: "Access token is missing. Please log in again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    try {
      // Prepare the selected quick links details
      const selectedQuickLinkDetails = quickLinkGroup.filter(
        (link) => selectedLinks.includes(link.projectLinkGroupID) // Match by projectLinkGroupID
      );
      console.log("Selected quick links are:", selectedQuickLinkDetails);

      // Extract only the userID values
      const quickLinkIDs = selectedQuickLinkDetails.map(
        (user) => user.projectLinkGroupID
      );

      // Call the service to update quick links group mapping
      await updateQuickLinksGroupMapping(
        accessToken,
        userGroupId || 0,
        quickLinkIDs, // Pass only the IDs
        tenantID,
        userData?.userID 
      );

      console.log("All quick link group mappings added successfully");
      onSave(selectedQuickLinkDetails); // Update local state
      onClose(); // Close modal
    } catch (error) {
      console.error("Error occurred during API call:", error);
      setError("An error occurred while saving quick links.");
    }
  };

  const fetchQuickLinkGroups = async () => {
  try {
    if (!accessToken) {
      setError("Access token is missing.");
      return;
    }

    setLoading(true);

    const data = await fetchUserGroupQuickLinkGroupMapping(
      accessToken,
      userGroupId || 0,
      tenantID
    );

    console.log("data of mapping USER-QUICKLINK-groups:", data);

    // Sort links so that checked ones come first
    const sortedQuickLinkGroup = data.sort(
      (a, b) => Number(b.isChecked) - Number(a.isChecked)
    );

    setQuickLinkGroup(sortedQuickLinkGroup);

    // Initialize selected links based on isChecked
    const initiallySelectedLinks = sortedQuickLinkGroup
      .filter((link) => link.isChecked)
      .map((link) => link.projectLinkGroupID); // Use projectLinkGroupID instead of quickLinksGroupID

    setSelectedLinks(initiallySelectedLinks);
  } catch (err) {
    setError("Failed to fetch Quick Link groups.");
  } finally {
    setLoading(false);
  }
};


  // useEffect for Quick Link Groups
  useEffect(() => {
    if (isOpen && accessToken) {
      fetchQuickLinkGroups();
    }
  }, [accessToken, userGroupId, isOpen]);

  useEffect(() => {
    // Reset to first page when search term changes
    setCurrentPage(1);
  }, [searchQuery]);


  // Close the modal and reset state
  const handleClose = () => {
    setSearchQuery(""); // Reset search query
    setCurrentPage(1); // Reset pagination
    setAllChecked(false); // Reset the "Check All" state
    onClose(); // Close the modal
  };

// Handle items per page change
const handleItemsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
  setItemsPerPage(Number(event.target.value));
  setCurrentPage(1); // Reset to the first page when items per page changes
};

// Handle page change
const handlePageChange = (page: number) => {
  if (page < 1 || page > totalPages) return;
  setCurrentPage(page);
};

// Handle search input change
const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
  setSearchQuery(e.target.value);
};


  // Filter QuickLinks based on search query
  const filteredQuickLinks = quickLinkGroup.filter((link) =>
    link.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination logic
  const totalSites = filteredQuickLinks.length;
  const totalPages = Math.ceil(totalSites / itemsPerPage); // Calculate total pages
  const indexOfLastSite = currentPage * itemsPerPage;
  const indexOfFirstSite = indexOfLastSite - itemsPerPage;
  const currentSites = filteredQuickLinks.slice(
    indexOfFirstSite,
    indexOfLastSite
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size={"lg"}>
      <ModalOverlay />
      <ModalContent maxWidth="900px" height="650px">
        <ModalHeader>Select Project Groups</ModalHeader>
        <ModalCloseButton />
        <hr style={{ border: "1px solid #black" }} />
        <Flex justify="space-between" align="center" p={4}>
          {/* Items per page selector */}
          <Select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            maxWidth="150px"
          >
            <option value={5}>5 items per page</option>
            <option value={6}>6 items per page</option>
          </Select>

          {/* Search input */}
          <FormControl maxWidth="300px">
            <Input
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search Project groups..."
            />
          </FormControl>
        </Flex>
        <ModalBody>
          <Table mt={4} className="Table">
            <Thead>
              <Tr>
                <Th width="50px">
                  <Checkbox
                    isChecked={
                      selectedLinks.length === quickLinkGroup.length &&
                      quickLinkGroup.length > 0
                    } // All selected
                    onChange={handleToggleAllLinks}
                    isIndeterminate={
                      selectedLinks.length > 0 &&
                      selectedLinks.length < quickLinkGroup.length // Partially selected
                    }
                  />
                </Th>
                <Th>Project Group Name</Th>
              </Tr>
            </Thead>
            <Tbody>
              {currentSites.map((link) => (
                <Tr key={link.projectLinkGroupID}>
                  <Td width="50px">
                    <Checkbox
                      isChecked={selectedLinks.includes(link.projectLinkGroupID)} // Reflect selection state
                      onChange={() => handleToggleLink(link.projectLinkGroupID)}
                    />
                  </Td>
                  <Td>{link.name}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          {/* Pagination Info */}
          <Flex justify="space-between" mt={4}>
            <Box>
              Showing {Math.min(totalSites, indexOfFirstSite + 1)} to{" "}
              {Math.min(indexOfLastSite, totalSites)} of {totalSites} entries
            </Box>

            {/* Pagination Controls */}
            <HStack>
              <Button
                onClick={() => handlePageChange(currentPage - 1)}
                isDisabled={currentPage === 1}
              >
                Previous
              </Button>
              <Select
                value={currentPage}
                onChange={(e) => handlePageChange(Number(e.target.value))}
                maxWidth="70px"
              >
                {Array.from({ length: totalPages }, (_, index) => (
                  <option key={index} value={index + 1}>
                    {index + 1}
                  </option>
                ))}
              </Select>
              <Button
                onClick={() => handlePageChange(currentPage + 1)}
                isDisabled={currentPage === totalPages}
              >
                Next
              </Button>
            </HStack>
          </Flex>
          {/* Save and Close Buttons */}
          <hr style={{ border: "1px solid #black", margin: "10px" }} />
          <Flex justify="flex-end" mt={3}>
            <Button onClick={handleSaveQuickLinks} colorScheme="blue" mr={3}>
              Save
            </Button>
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default QuickLinksModal;
